/**
 * Supabase realtime subscription manager that never throws into React.
 *
 * Plain JS (typed by realtime-core.d.ts) so node:test can exercise it with a fake client.
 *
 * Why it exists: supabase-js `client.channel(topic)` returns the EXISTING channel when one with the
 * same topic is still registered, and `removeChannel` is async. The previous hooks re-ran their
 * effect on every render (an inline options object was a dependency) and on StrictMode remounts,
 * so `.on('postgres_changes', ...)` was called on an already-subscribed channel and threw
 * "cannot add `postgres_changes` callbacks ... after `subscribe()`" inside a layout effect, which
 * the app ErrorBoundary turned into a crashed page (/orders, /dashboard, /inventory).
 *
 * Here every manager uses its own unique topic, setup errors are caught, a failed or closed channel
 * is torn down and retried with capped exponential backoff, and status is reported as
 *   'connecting' | 'live' | 'unavailable' | 'off'
 * Realtime is an enhancement: the pages keep working from their normal queries without it.
 */

let seq = 0;

/** Backoff for retry n (1-based): base * 2^(n-1), capped at max. */
export function backoffDelay(attempt, baseMs = 2000, maxMs = 60000) {
  const n = Math.max(1, attempt);
  return Math.min(maxMs, baseMs * 2 ** Math.min(n - 1, 20));
}

/**
 * @param {object} opts
 * @param {{ channel: Function, removeChannel: Function }} opts.client  supabase client (or a fake)
 * @param {string} opts.name                 topic prefix (a unique suffix is added)
 * @param {Array<{ table: string, filter?: string, event?: string, schema?: string }>} opts.bindings
 * @param {(table: string, payload: any) => void} opts.onEvent
 * @param {(state: { status: string, attempt: number, retryInMs: number | null, error: string | null }) => void} [opts.onStatus]
 * @param {number} [opts.baseDelayMs]
 * @param {number} [opts.maxDelayMs]
 * @param {Function} [opts.setTimer]
 * @param {Function} [opts.clearTimer]
 * @param {(msg: string, err?: unknown) => void} [opts.log]
 */
export function createRealtimeManager(opts) {
  const {
    client,
    name,
    bindings,
    onEvent,
    onStatus = () => {},
    baseDelayMs = 2000,
    maxDelayMs = 60000,
    setTimer = (fn, ms) => setTimeout(fn, ms),
    clearTimer = (t) => clearTimeout(t),
    log = () => {},
  } = opts;

  let channel = null;
  let timer = null;
  let attempt = 0;
  let stopped = true;
  let state = { status: 'off', attempt: 0, retryInMs: null, error: null };

  function emit(next) {
    state = { ...state, ...next };
    try {
      onStatus(state);
    } catch (e) {
      log('realtime status listener failed', e);
    }
  }

  function teardown() {
    const ch = channel;
    channel = null;
    if (!ch) return;
    try {
      const p = client.removeChannel(ch);
      if (p && typeof p.catch === 'function') p.catch((e) => log('realtime removeChannel failed', e));
    } catch (e) {
      log('realtime removeChannel failed', e);
    }
  }

  function fail(reason) {
    if (stopped || timer) return;
    teardown();
    attempt += 1;
    const delay = backoffDelay(attempt, baseDelayMs, maxDelayMs);
    const error = reason ? String((reason && reason.message) || reason) : null;
    log(`realtime ${name} unavailable (attempt ${attempt}); retrying in ${delay} ms${error ? `: ${error}` : ''}`);
    emit({ status: 'unavailable', attempt, retryInMs: delay, error });
    timer = setTimer(() => {
      timer = null;
      connect();
    }, delay);
  }

  function connect() {
    if (stopped) return;
    let ch;
    try {
      ch = client.channel(`${name}:${++seq}`);
      channel = ch;
      for (const b of bindings) {
        const filter = { event: b.event || '*', schema: b.schema || 'public', table: b.table };
        if (b.filter) filter.filter = b.filter;
        ch.on('postgres_changes', filter, (payload) => {
          try {
            onEvent(b.table, payload);
          } catch (e) {
            log(`realtime ${name} handler for ${b.table} failed`, e);
          }
        });
      }
      ch.subscribe((status, err) => {
        if (stopped || channel !== ch) return; // a channel we already replaced or removed
        if (status === 'SUBSCRIBED') {
          attempt = 0;
          emit({ status: 'live', attempt: 0, retryInMs: null, error: null });
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          fail(err || status);
        }
      });
    } catch (e) {
      fail(e);
    }
  }

  return {
    start() {
      if (!stopped) return;
      stopped = false;
      attempt = 0;
      emit({ status: 'connecting', attempt: 0, retryInMs: null, error: null });
      connect();
    },
    stop() {
      if (stopped) return;
      stopped = true;
      if (timer) clearTimer(timer);
      timer = null;
      teardown();
      emit({ status: 'off', attempt: 0, retryInMs: null, error: null });
    },
    getState() {
      return state;
    },
  };
}
